import { VStack, HStack, Box, WarningIcon } from 'native-base';
import { Caption, H2 } from '~/shared/Typography';
import { Plan, useGetMember, useI18n } from '@springbig/shared';
import React, { useEffect, useState } from 'react';
import { useAppNavigate } from '~/pages';
import Button from '~/shared/Button';
import { useGeneralStyles, useMenuStyles } from '~/shared/styles';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { useToast } from '~/hooks/toast';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Pressable from '~/shared/Pressable';
import CreditCardForm from '~/shared/CreditCardForm/CreditCardForm';
import CancelSubscription from '~/pages/subscriptions/components/CancelSubscription';
import UseManageFlow from '~/pages/subscriptions/hooks/useManageFlow';
import { format } from 'date-fns';

type ManageFlowProps = {
    plan: Plan;
};

type Pages = 'update email' | 'change card' | 'cancel subscription';

type ManageOption = {
    page: Pages;
    title?: string;
    icon?: IconDefinition;
    isDisabled?: boolean;
    hasWarning?: boolean;
};

const ManageFlow = ({ plan }: ManageFlowProps) => {

    const { navigate, params } = useAppNavigate();
    const gStyle = useGeneralStyles();
    const {
        icon,
        card_background,
        card_border,
        card_text
    } = useMenuStyles();
    const { successToast } = useToast();
    const { t } = useI18n();
    const { refetch: refreshMember } = useGetMember();

    const [isInSubPage, setIsInSubPage] = useState<boolean>(false);
    const [isUpdateEmailScreen, setIsUpdateEmailScreen] = useState<boolean>(false);
    const [isChangeCardScreen, setIsChangeCardScreen] = useState<boolean>(false);
    const [isCancelSubscriptionScreen, setIsCancelSubscriptionScreen] = useState<boolean>(false);

    useEffect(() => {
        if (isUpdateEmailScreen || isChangeCardScreen || isCancelSubscriptionScreen) {
            setIsInSubPage(true);
        }
        else {
            setIsInSubPage(false);
        }
    }, [isInSubPage, isUpdateEmailScreen, isChangeCardScreen, isCancelSubscriptionScreen]);

    const onCancel = (): void => {
        navigate(`w/${params.wallet_id}/subscriptions`);
    };

    const onChangePage = (page: Pages): void => {
        switch (page) {
            case 'update email':
                setIsUpdateEmailScreen(true);
                break;
            case 'change card':
                setIsChangeCardScreen(true);
                break;
            case 'cancel subscription':
                setIsCancelSubscriptionScreen(true);
                break;
            default:
                console.error(`ManageFlow > ${page} does not exist`);
                break;
        }
    };

    const onSuccessfulEmailUpdate = (): void => {
        setIsUpdateEmailScreen(false);
        successToast('email successfully updated');
    };

    const onSuccessfulCardUpdate = (): void => {
        setIsChangeCardScreen(false);
        successToast('card successfully updated');
    };

    const onSuccessfulCancellation = (): void => {
        setIsCancelSubscriptionScreen(false);
        refreshMember();
        successToast('subscription successfully cancelled');
    };

    const {
        apiError,
        isLoadingUpdateCard,
        isLoadingCancelSubscription,
        onSubmitCancelSubscription,
        onSubmitUpdateCreditCard,
    } = UseManageFlow({
        plan,
        onSuccessfulCardUpdate,
        onSuccessfulCancellation
    });

    const generateActiveUntilDate = (terminatedAt: Date): string => {

        return t('subscriptions.active_until', [format(new Date(terminatedAt), 'PPP')]);
    };

    const manageOptions: ManageOption[] = [
        {
            page: 'update email',
            title: t('subscriptions.manage.update_email'),
            icon: faChevronRight,
        },
        {
            page: 'change card',
            title: t('subscriptions.manage.change_card'),
            icon: faChevronRight,
            hasWarning: plan.subscription?.status === 'suspended',
        },
        {
            page: 'cancel subscription',
            icon: plan.subscription?.status !== 'active' ? undefined : faChevronRight,
            title: plan.subscription?.status !== 'active' ? generateActiveUntilDate(plan.subscription.terminated_at) : t('subscriptions.manage.cancel_subscription'),
            isDisabled: plan.subscription?.status !== 'active',
        },
    ];

    return (
        <Box>
            { !isInSubPage ?
                <VStack>
                    <H2 textAlign={'center'}>{ t('subscriptions.manage.title', [plan.name]) }</H2>
                    <VStack>
                        { manageOptions.map((option) => {

                            return (<Pressable
                                key={option.title}
                                flexDirection={'row'}
                                justifyContent={option.icon ? 'space-between' : 'center'}
                                bg={card_background}
                                borderColor={card_border}
                                borderWidth={1}
                                isDisabled={option.isDisabled}
                                opacity={option.isDisabled ? .5 : 1}
                                p={4}
                                onPress={() => onChangePage(option.page)}
                            >
                                <Caption color={card_text}>
                                    { option.title || option.page }
                                </Caption>
                                <HStack space={2}>
                                    { option.hasWarning &&
                                        <WarningIcon size={5} color={'red'} />
                                    }
                                    { option.icon &&
                                        <SpIcon
                                            color={icon}
                                            icon={option.icon}
                                        />
                                    }
                                </HStack>
                            </Pressable>);
                        }
                        )}
                    </VStack>
                    <Button
                        bg={gStyle.button_neutral_background}
                        _text={{ color: gStyle.button_neutral_text }}
                        onPress={onCancel}
                        mx={4}
                        marginTop={8}
                    >
                        { t('subscriptions.back_button') }
                    </Button>
                </VStack>
                :
                isUpdateEmailScreen ? (
                    <Box>
                        <H2 textAlign={'center'}>
                            { t('subscriptions.update_email_info') }
                        </H2>
                        <EmailVerificationForm
                            t={t}
                            isRequiredTerms
                            walletId={params.wallet_id}
                            onCancel={() => setIsUpdateEmailScreen(false)}
                            onProceed={onSuccessfulEmailUpdate}
                        />
                    </Box>
                ) : isChangeCardScreen ? (
                    <Box px={4}>
                        <H2 textAlign={'center'}>
                            { t('credit_card.add_card')}
                        </H2>
                        <CreditCardForm
                            onCancel={() => setIsChangeCardScreen(false)}
                            onProceed={(data) => onSubmitUpdateCreditCard(data)}
                            isLoading={isLoadingUpdateCard}
                            error={apiError}
                            t={t}
                        />
                    </Box>
                ) : (
                    <Box px={4}>
                        <CancelSubscription
                            onCancel={() => setIsCancelSubscriptionScreen(false)}
                            onProceed={onSubmitCancelSubscription}
                            isLoading={isLoadingCancelSubscription}
                            error={apiError}
                        />
                    </Box>
                )
            }
        </Box>
    );
};

export default ManageFlow;
